import type { Werktuig } from './werkcyclus'
import type { HeenEnWeerTempo, HeenEnWeerTruck } from './heenEnWeer'

/**
 * Hoe snel de Matics bewegen: de waarden van de typebladen van Linde
 * (`public/productsheets/*.pdf`, rij 5.1–5.4), sinds 21 oktober zonder
 * versnellingsfactor.
 *
 * Eerder liepen ze bewust sneller dan de echte machine, "want een bezoeker
 * kijkt hooguit een halve minuut" — de L-MATIC hefde zo veertien keer te
 * snel. De feedback was dat het er daardoor als animatie uitzag. Een rondgang
 * duurt nu twee tot drie keer zo lang, maar een K- of R-MATIC staat nooit
 * stil: wie een halve minuut kijkt, ziet altijd heffen, dalen of vorken.
 * De snelheidsschuif in de Studio blijft de noodknop.
 *
 * Versnellingen staan niet op de typebladen. 0,5 m/s² voor het rijden en
 * 0,3 m/s² voor de hydrauliek is rustig, zoals een autonome truck tussen
 * mensen rijdt.
 *
 * Puur, zonder three.js: `scripts/controleer-bewegingen.mjs` toetst hiermee de
 * werkcyclus buiten de browser.
 */

export type MaticModel = 'K-Matic' | 'T-Matic' | 'R-Matic' | 'L-Matic'

export const RIJ_A = 0.5
export const HYDRAULIEK_A = 0.3

/** Vorkmaten die zowel het model als de werkcyclus nodig heeft. */
export const R_VORKMATEN = { blad: 0.045, reach: 0.537 }
export const K_VORKMATEN = { blad: 0.05, schuif: 1.35 }

/**
 * De werkcyclus van de K- en R-MATIC (`werkcyclus.ts`):
 *
 * - R-MATIC: een kwartslag de stelling in (90° in ±4 s), 0,9 m ernaartoe —
 *   de punten van zijn poten 3 cm voor de stelling — en de mast 537 mm naar
 *   voren. Hij rijdt met het blad op 0,5 m: een pallet (1200 dwars) ligt dan
 *   vrij boven zijn poten. Met de vorken naar A rijdt hij naar B met de
 *   aandrijving voorop (1,95 m/s) en naar A met de vorken voorop (0,75 m/s).
 * - K-MATIC: blijft in zijn baan langs de stelling en schuift de vorken
 *   1,35 m dwars. Het blad op 0,62 m: de pallet vrij boven het chassis.
 */
export const WERKTUIG: Partial<Record<MaticModel, Werktuig>> = {
  'R-Matic': {
    draait: true, naar: 0.9, rij: 0.5, blad: R_VORKMATEN.blad,
    heffen: { last: 0.52, leeg: 0.66 }, dalen: { last: 0.55, leeg: 0.44 }, hefA: HYDRAULIEK_A,
    uit: { weg: R_VORKMATEN.reach, v: 0.2, a: HYDRAULIEK_A },
    draai: { w: 0.6, a: 0.7 }, nader: { v: 0.4, a: 0.4 },
    rijden: { naarB: 1.95, naarA: 0.75, a: RIJ_A },
  },
  'K-Matic': {
    draait: false, naar: 0, rij: 0.62, blad: K_VORKMATEN.blad,
    heffen: { last: 0.4, leeg: 0.4 }, dalen: { last: 0.4, leeg: 0.4 }, hefA: HYDRAULIEK_A,
    uit: { weg: K_VORKMATEN.schuif, v: 0.29, a: HYDRAULIEK_A },
    draai: { w: 0.6, a: 0.7 }, nader: { v: 0.4, a: 0.4 },
    rijden: { naarB: 2.0, naarA: 2.0, a: RIJ_A },
  },
}

/**
 * De C-MATIC-pendel (`pendel.ts`): 4,3 km/h beladen en 5,4 leeg volgens het
 * typeblad; draait op de plek, 180° in ±4,5 s; de adapterplaat tilt 60 mm in
 * 1,5 s.
 */
export const C_TEMPO = { beladen: 1.19, leeg: 1.5, a: RIJ_A, draai: 1.0, draaiA: 1.2, hef: 1.5 }

/** T-MATIC core (1153): 7,2 km/h; heffen 0,05 / 0,06 m/s, dalen 0,10 / 0,08 (met / zonder last). */
export const T_TEMPO: HeenEnWeerTempo = {
  rijden: { last: 2.0, leeg: 2.0 }, kruip: 0.5, a: RIJ_A,
  heffen: { last: 0.05, leeg: 0.06 }, dalen: { last: 0.1, leeg: 0.08 }, hefA: HYDRAULIEK_A,
}

/**
 * L-MATIC core (1195-01): 6,0 km/h beladen en 7,2 leeg in automatisch bedrijf
 * (voetnoot 2 bij rij 5.1; 4 km/h is onderhoud); heffen 0,19 / 0,30 m/s,
 * dalen 0,31 / 0,24.
 */
export const L_TEMPO: HeenEnWeerTempo = {
  rijden: { last: 1.67, leeg: 2.0 }, kruip: 0.5, a: RIJ_A,
  heffen: { last: 0.19, leeg: 0.3 }, dalen: { last: 0.31, leeg: 0.24 }, hefA: HYDRAULIEK_A,
}

/**
 * Eén pallet heen en weer (`heenEnWeer.ts`). Maten uit de modellen: het
 * midden van de pallet op de vorken (`p`), hoe ver hij achteruit moet om de
 * vorken eruit te halen, en de vorkhoogtes. De T-MATIC heft h3 = 120 mm.
 *
 * - T-MATIC: vorken naar de camera (zin −1), aan beide kanten op de vloer;
 *   aan de staartkant wacht hij met de vorken erin.
 * - L-MATIC: vorken van de camera af (zin +1), zodat zijn achterkant met het
 *   display en het logo naar de camera staat; aan de verre kant een
 *   overslagstation van 1 m, aan de camerakant de vloer.
 */
export const HEEN_EN_WEER: Record<'T-Matic' | 'L-Matic', HeenEnWeerTruck> = {
  'T-Matic': {
    zin: -1, p: 0.42, uitrij: 1.27, achter: 0.99, vorkTop: 0.085, rij: 0.12, raak: 0.015,
    kopHoogte: 0, staartUitrijden: false, tempo: T_TEMPO,
  },
  'L-Matic': {
    zin: 1, p: 0.245, uitrij: 1.3, achter: 0.9, vorkTop: 0.086, rij: 0.12, raak: 0.014,
    kopHoogte: 1.0, staartUitrijden: true, tempo: L_TEMPO,
  },
}
